import { z } from "zod";
import { SpotifyIdSchema } from "./common.ts";

/**
 * Location kinds for globe pins. v1 renders `country` only; `city` / `studio`
 * are reserved so the client can key styling off `kind` without a rewrite.
 */
export const GlobePointKindSchema = z.enum(["country", "city", "studio"]);
export type GlobePointKind = z.infer<typeof GlobePointKindSchema>;

export const GlobePointSchema = z.object({
  /** Stable id, e.g. `country:FR`. */
  id: z.string().min(1),
  kind: GlobePointKindSchema,
  lat: z.number(),
  lng: z.number(),
  label: z.string(),
  artistCount: z.number().int().nonnegative(),
  /** Artists at this point — for the click-through list. */
  artistIds: z.array(SpotifyIdSchema),
  /** ISO alpha-2 when kind is country; omitted for future kinds. */
  countryCode: z.string().length(2).optional(),
});
export type GlobePoint = z.infer<typeof GlobePointSchema>;

export const GlobePlaylistSchema = z.object({
  id: SpotifyIdSchema,
  name: z.string(),
  coverUrl: z.string().nullable(),
  /**
   * Dedup'd country codes, sorted by artist count desc so the client can take
   * the top 40 for arcs and note "+N more" from the remainder.
   */
  artistCountryCodes: z.array(z.string().length(2)),
  /** Artists on this playlist that have a known country (for filtered lists). */
  artistIds: z.array(SpotifyIdSchema),
  trackCount: z.number().int().nonnegative(),
});
export type GlobePlaylist = z.infer<typeof GlobePlaylistSchema>;

/**
 * Generic arc payload — any feature (playlist lens, future recording journeys)
 * feeds the same layer shape.
 */
export const GlobeArcSchema = z.object({
  startLat: z.number(),
  startLng: z.number(),
  endLat: z.number(),
  endLng: z.number(),
  color: z.string(),
  altitude: z.number().optional(),
});
export type GlobeArc = z.infer<typeof GlobeArcSchema>;

export const GlobeResponseSchema = z.object({
  points: z.array(GlobePointSchema),
  playlists: z.array(GlobePlaylistSchema),
});
export type GlobeResponse = z.infer<typeof GlobeResponseSchema>;
